<script setup lang="ts">
import isFloat from 'validator/es/lib/isFloat'
import type { Props } from './InputShellText.vue'

const t2 = useT2Base({
  Percentage: 'Procentas',
  'Must be more than': 'Turi būti daugiau nei',
  'Must be a positive number': 'Turi būti teigiamas skaičius',
  'Must be a number': 'Turi būti skaičius',
  'Must be less than': 'Turi būti mažiau nei',
  VAT: 'PVM',
  'Before VAT': 'Prieš PVM',
  'Weight grams': 'Svoris gramai',
  Volume: 'Tūris',
  Distance: 'Atstumas',
  Grams: 'Gramai',
  Price: 'Kaina',
  Number: 'Skaičius',
  'Too small': 'Per mažas',
  'Too big': 'Per didelis',
})

type NumberType = 'cm3' | 'cm' | 'g' | '%' | 'price' | 'number'
const props = defineProps<{
  label?: stringI18n
  optional?: boolean
  disabled?: boolean
  placeholder?: stringI18n
  type?: NumberType | None
  showVat?: 'never'
  beforeLabel?: 'never'
  afterLabel?: 'never'
  priceVat?: true
  before?: string
  after?: string
  activated?: boolean
  decimal?: number
  allowZero?: boolean
  exclusive?: boolean
  allowMinEqual?: 'never'
  min?: number
  max?: number
  step?: number
  validate?: MyInputError<number>[] | None
  width?: Props['width']
}>()

const holder = defineModel<number | None>({ default: 0 })

const emit = defineEmits<{ focus: [e: boolean] }>()
const focus = shallowRef(false)
const elem = useTemplateRef('elem')

type UiValue = {
  label?: string
  ph?: string
  after?: string
  before?: string
  min?: number
  max?: number
  decimal?: number
}

const map: Record<NumberType, UiValue> = {
  '%': { before: '%', ph: '100', label: t2('Percentage'), min: 0, max: 100 },
  cm: { label: t2('Distance'), after: 'cm', ph: ts`100cm` },
  cm3: { after: 'cm3', label: t2('Volume'), ph: ts`400cm3` },
  g: { after: t2('Grams'), label: t2('Weight grams'), ph: ts`200g` },
  price: { before: '€', label: t2('Price'), ph: ts`19.99`, decimal: 2 },
  number: { label: t2('Number'), ph: '49' },
}

const ui = computed(() => {
  const { type, label, min, max, placeholder, after, before, decimal, step } = props
  const ui: UiValue = (type && map[type]) || map.number
  return {
    label: label ?? ui.label ?? t2('Number'),
    ph: placeholder ?? ui.ph,
    after: after ?? ui.after,
    before: before ?? ui.before,
    max: max ?? ui.max,
    min: min ?? ui.min,
    decimal: decimal ?? ui.decimal ?? 0,
    step: step ?? 1,
  }
})

function validateMin(min: number, allowZero: boolean, exclusive: boolean): MyInputError<number> {
  return {
    isErr: allowZero || !exclusive ? (i) => min > +i : (i) => min >= +i,
    msg: min === 0 && !allowZero ? t2('Must be a positive number') : ts(t2('Must be more than'), min),
  }
}
function validateMax(max: number, allowZero: boolean, exclusive: boolean): MyInputError<number> {
  return {
    isErr: allowZero || !exclusive ? (i) => max < +i : (i) => max <= +i,
    msg: ts(t2('Must be less than'), max),
  }
}

const myInput = useMyInput({
  label: () => ui.value.label,
  optional: () => props.optional,
  value: () => holder.value ?? 0,
  empty: (i) => {
    return props.allowZero ? false : !i
  },
  validate(): MyInputError<number>[] {
    const { allowZero, validate, exclusive } = props
    const { min, max, decimal } = ui.value
    return [
      {
        msg: t2('Must be a number'),
        isErr: (i) => !helpers.isNumeric(i) || !isFloat(helpers.toString(i)),
      },
      {
        msg: t2('Too small'),
        isErr: (i) => !eqeq(i.toFixed(decimal), i),
      },
      {
        msg: t2('Too big'),
        isErr: (i) => Number.isInteger(i) && !Number.isSafeInteger(i),
      },
      min && validateMin(min, allowZero, exclusive),
      max && validateMax(max, allowZero, exclusive),
      ...helpers.arr(validate),
    ] satisfies MyInputError<number>[]
  },
})

const error = useMyInputValidate(myInput, {
  disabled: () => props.disabled,
  focus: () => focus.value,
  activated: props.activated,
})

defineExpose({
  ...myInput.expose(),
  focus() {
    elem.value?.focus()
  },
  blur() {
    elem.value?.blur()
  },
})

function focused(value: boolean) {
  emit('focus', value)
  focus.value = value
}

const price = computed(() => {
  const taxful = helpers.toNumber(holder.value)
  const totalInCents = taxful * 100
  const taxlessCents = Math.floor((totalInCents * 100) / 121)
  const taxesCents = Math.floor(totalInCents - taxlessCents)
  return `${(taxlessCents / 100).toFixed(2)} ${t2('Before VAT')} | ${(taxesCents / 100).toFixed(2)} ${t2('VAT')}`
})

const weight = computed(() => {
  const kg = helpers.toNumber(holder.value) / 1000
  const truncatedKg = Math.floor(kg * 100) / 100
  return `${truncatedKg.toFixed(2)}kg`
})

const allowWhole = '1234567890'
const allowDecibal = allowWhole + '.'
function keypress(e: KeyboardEvent) {
  const allow = ui.value.decimal > 0 ? allowDecibal : allowWhole
  if (!allow.includes(e.key)) return e.preventDefault()
  if (e.key === '.' && !Number.isInteger(holder.value)) return e.preventDefault()
}

function keydown(e: KeyboardEvent) {
  if (!e.shiftKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
  e.preventDefault()

  const { max, min } = ui.value

  const next = helpers.fit(
    helpers.add(
      { decimal: ui.value.decimal },
      e.key === 'ArrowUp' ? 10 : -10,
      myInputValue(e, (i) => +(i ?? 0))
    ),
    min ?? Number.NEGATIVE_INFINITY,
    max ?? Number.POSITIVE_INFINITY
  )

  if (next !== holder.value) holder.value = next
}

const active = computed(() => !!(holder.value || error.value))
</script>

<template>
  <InputShellText :labelid="myInput.id" :label="ui.label" :before="ui.before" :after="ui.after" :optional :disabled :error :active :focus :width>
    <template #default="{ classInput }">
      <input
        :id="myInput.id"
        ref="elem"
        v-bind="$attrs"
        v-model="holder"
        dir="ltr"
        tabindex="0"
        spellcheck="false"
        autocapitalize="off"
        :disabled="disabled"
        :placeholder="ui.ph"
        type="number"
        :step="ui.step"
        :class="classInput"
        @focus.passive="() => focused(true)"
        @blur.passive="() => focused(false)"
        @keydown="(e) => keydown(e)"
        @keypress="(e) => keypress(e)"
      />
    </template>

    <template v-if="priceVat" #after="{ classAfter }">
      <span v-memo="[price]" :class="classAfter">{{ price }}</span>
    </template>

    <template v-else-if="type === 'g'" #after="{ classAfter }">
      <span v-memo="[weight]" :class="classAfter">{{ weight }}</span>
    </template>

    <template v-for="(_, name) in $slots" #[name]="slotData"><slot :name="name" v-bind="slotData" /></template>
  </InputShellText>
</template>
